import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import './BBMappingPage.css'; // Reuse same styles

/**
 * BOQDepMappingPage - Admin tool for mapping master_items' boq_description entries
 * to real master_items as base_dependencies.
 *
 * Phase 11 of Unified Item Language spec.
 * Replaces /admin/boq-mapping.
 *
 * Each row = one master_item that has boq_description.entries[].
 * For each entry, admin maps it to one or more existing master_items.
 * If no matching master_item exists, admin can create one via modal.
 * Each add/remove writes directly to master_items.item_options (immediate-write pattern,
 * matching BundleReviewPage and other admin pages — no local-only state to lose).
 */

// ─── Create Master Item Modal ──────────────────────────────────────
const CreateMasterItemModal = ({
  onClose,
  onCreated,
  itemTypes,
  projectPhases,
  costTypes,
  bbChapters,
}) => {
  const [form, setForm] = useState({
    item_name: '',
    item_type_id: '',
    project_phase_id: '',
    cost_type_id: '',
    bb_chapter_id: '',
    estimated_cost: '',
    include_in_boq: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!form.item_name.trim()) {
      setError('שם פריט נדרש');
      return;
    }
    if (!form.item_type_id) {
      setError('סוג פריט נדרש');
      return;
    }
    if (!form.cost_type_id) {
      setError('סוג מדידה נדרש');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const insertData = {
        item_name: form.item_name.trim(),
        item_type_id: form.item_type_id,
        cost_type_id: form.cost_type_id,
        include_in_boq: form.include_in_boq,
        is_bundle: false,
      };
      if (form.project_phase_id) insertData.project_phase_id = form.project_phase_id;
      if (form.bb_chapter_id) insertData.bb_chapter_id = form.bb_chapter_id;
      if (form.estimated_cost) insertData.estimated_cost = parseFloat(form.estimated_cost);

      const { data, error: insertError } = await supabase
        .from('master_items')
        .insert(insertData)
        .select('id, item_name, item_type_id, cost_type_id, bb_chapter_id, include_in_boq')
        .single();

      if (insertError) throw insertError;
      onCreated(data);
      onClose();
    } catch (err) {
      console.error('Error creating master item:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 24,
          minWidth: 400,
          maxWidth: 500,
          direction: 'rtl',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 16px 0' }}>יצירת פריט מאסטר חדש</h3>

        {error && (
          <div
            style={{
              color: '#dc2626',
              fontSize: 13,
              marginBottom: 12,
              padding: '8px 12px',
              background: '#fef2f2',
              borderRadius: 6,
            }}
          >
            {error}
          </div>
        )}

        {/* Item Name */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            שם פריט *
          </label>
          <input
            type="text"
            value={form.item_name}
            onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              boxSizing: 'border-box',
            }}
            autoFocus
          />
        </div>

        {/* Item Type */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            סוג פריט *
          </label>
          <select
            value={form.item_type_id}
            onChange={e => setForm(f => ({ ...f, item_type_id: e.target.value }))}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              boxSizing: 'border-box',
            }}
          >
            <option value="">בחר סוג...</option>
            {itemTypes.map(t => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {/* Project Phase */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            שלב פרויקט
          </label>
          <select
            value={form.project_phase_id}
            onChange={e => setForm(f => ({ ...f, project_phase_id: e.target.value }))}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              boxSizing: 'border-box',
            }}
          >
            <option value="">לא נבחר</option>
            {projectPhases.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {/* Cost Type (measurement unit) */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            יחידת מדידה *
          </label>
          <select
            value={form.cost_type_id}
            onChange={e => setForm(f => ({ ...f, cost_type_id: e.target.value }))}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              boxSizing: 'border-box',
            }}
          >
            <option value="">בחר יחידה...</option>
            {costTypes.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* BB Chapter */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            פרק ספר כחול
          </label>
          <select
            value={form.bb_chapter_id}
            onChange={e => setForm(f => ({ ...f, bb_chapter_id: e.target.value }))}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              boxSizing: 'border-box',
            }}
          >
            <option value="">לא נבחר</option>
            {bbChapters.map(c => (
              <option key={c.id} value={c.id}>
                {c.code} - {c.name_he}
              </option>
            ))}
          </select>
        </div>

        {/* Estimated Cost */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            עלות משוערת
          </label>
          <input
            type="number"
            value={form.estimated_cost}
            onChange={e => setForm(f => ({ ...f, estimated_cost: e.target.value }))}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              boxSizing: 'border-box',
            }}
            placeholder="₪"
          />
        </div>

        {/* Include in BOQ */}
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={form.include_in_boq}
            onChange={e => setForm(f => ({ ...f, include_in_boq: e.target.checked }))}
            id="include-boq-check"
          />
          <label htmlFor="include-boq-check" style={{ fontSize: 13 }}>
            כלול בכתב כמויות
          </label>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start' }}>
          <button className="bb-action-btn primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'שומר...' : 'צור פריט'}
          </button>
          <button className="bb-action-btn reject" onClick={onClose} disabled={saving}>
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Searchable Master Item Picker ─────────────────────────────────
// Dropdown to search and select from existing master_items
const MasterItemPicker = ({ allMasterItems, excludeIds, onSelect, onCreateNew }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const panelRef = React.useRef(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClick = e => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const filtered = useMemo(() => {
    let items = allMasterItems;
    if (search.trim()) {
      const term = search.toLowerCase();
      items = items.filter(mi => mi.item_name.toLowerCase().includes(term));
    }
    // Exclude already-selected items
    items = items.filter(mi => !excludeIds.includes(mi.id));
    return items;
  }, [allMasterItems, search, excludeIds]);

  if (!open) {
    return (
      <button
        className="bb-action-btn primary"
        style={{ fontSize: 11, padding: '2px 8px', whiteSpace: 'nowrap' }}
        onClick={() => setOpen(true)}
      >
        + הוסף תלות
      </button>
    );
  }

  return (
    <div
      ref={panelRef}
      style={{
        position: 'absolute',
        zIndex: 100,
        background: '#fff',
        border: '1px solid #cbd5e1',
        borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        direction: 'rtl',
        width: 320,
        maxHeight: 360,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Search input */}
      <div style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="חפש פריט מאסטר..."
          autoFocus
          style={{
            width: '100%',
            padding: '6px 10px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 13,
            boxSizing: 'border-box',
          }}
        />
      </div>
      {/* Results */}
      <div style={{ overflowY: 'auto', maxHeight: 260 }}>
        {filtered.map(mi => {
          const isExcluded = excludeIds.includes(mi.id);
          return (
            <div
              key={mi.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px 10px',
                fontSize: 12,
                cursor: isExcluded ? 'default' : 'pointer',
                borderBottom: '1px solid #f1f5f9',
                opacity: isExcluded ? 0.4 : 1,
                background: isExcluded ? '#f8fafc' : 'transparent',
              }}
              onClick={() => {
                if (!isExcluded) {
                  onSelect(mi);
                  setOpen(false);
                  setSearch('');
                }
              }}
            >
              <span style={{ flex: 1 }}>{mi.item_name}</span>
              <span style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                {mi.is_bundle && (
                  <span
                    style={{
                      fontSize: 9,
                      color: '#fff',
                      background: '#8b5cf6',
                      borderRadius: 3,
                      padding: '0 4px',
                    }}
                  >
                    חבילה
                  </span>
                )}
                {mi.include_in_boq && <span style={{ fontSize: 10, color: '#16a34a' }}>BOQ</span>}
              </span>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div
            style={{ padding: '12px 10px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}
          >
            לא נמצאו תוצאות
          </div>
        )}
      </div>
      {/* Create new button */}
      <div style={{ padding: 8, borderTop: '1px solid #e2e8f0' }}>
        <button
          className="bb-action-btn primary"
          style={{ width: '100%', fontSize: 12 }}
          onClick={() => {
            setOpen(false);
            setSearch('');
            onCreateNew();
          }}
        >
          + צור פריט חדש
        </button>
      </div>
    </div>
  );
};

// ─── Main Page Component ───────────────────────────────────────────
const BOQDepMappingPage = () => {
  // Data state
  const [parentItems, setParentItems] = useState([]); // master_items with boq_description
  const [allMasterItems, setAllMasterItems] = useState([]); // all master_items for picker
  const [itemTypes, setItemTypes] = useState([]);
  const [projectPhases, setProjectPhases] = useState([]);
  const [costTypes, setCostTypes] = useState([]);
  const [costTypesMap, setCostTypesMap] = useState({});
  const [oldCategories, setOldCategories] = useState({});
  const [bbChapters, setBbChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // DB-persisted suggestions: parentItemId -> [{ id, suggested_dep_id, confidence, ai_reasoning, status }]
  const [suggestions, setSuggestions] = useState({});
  // DB-persisted reviewed items (Set of master_item_ids marked as "done")
  const [reviewedItems, setReviewedItems] = useState(new Set());
  const [apiKey] = useState(process.env.REACT_APP_CLAUDE_API_KEY || '');
  const [aiRunning, setAiRunning] = useState(false);
  const [aiProgress, setAiProgress] = useState({ current: 0, total: 0 });

  // UI state
  const [filter, setFilter] = useState('todo');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalCallback, setCreateModalCallback] = useState(null);
  const [expandedBundles, setExpandedBundles] = useState(new Set());

  // ─── Data fetching ───────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        parentItemsRes,
        itemTypesRes,
        phasesRes,
        costTypesRes,
        categoriesRes,
        chaptersRes,
        suggestionsRes,
        reviewedRes,
      ] = await Promise.all([
        // All master items (used for both parent list and picker/child resolution)
        supabase
          .from('master_items')
          .select(
            'id, item_name, include_in_boq, is_bundle, bundle_items, item_type_id, cost_type_id, bb_chapter_id, boq_description, item_options',
          )
          .order('item_name'),
        supabase.from('reference_item_types').select('id, name').order('name'),
        supabase
          .from('reference_project_phases')
          .select('id, name, display_order')
          .order('display_order'),
        supabase.from('reference_cost_types').select('id, name'),
        supabase.from('reference_boq_categories').select('id, name, category_number'),
        supabase.from('reference_bb_chapters').select('id, code, name_he, depth').order('code'),
        // Load persisted AI suggestions
        supabase.from('dep_mapping_suggestions').select('*'),
        // Load reviewed items
        supabase.from('dep_mapping_reviewed').select('master_item_id'),
      ]);

      // parentItemsRes is now the single all-items query
      if (parentItemsRes.error) throw parentItemsRes.error;
      if (itemTypesRes.error) throw itemTypesRes.error;
      if (phasesRes.error) throw phasesRes.error;
      if (costTypesRes.error) throw costTypesRes.error;
      if (categoriesRes.error) throw categoriesRes.error;
      if (chaptersRes.error) throw chaptersRes.error;
      if (suggestionsRes.error) throw suggestionsRes.error;
      if (reviewedRes.error) throw reviewedRes.error;

      const allItems = parentItemsRes.data || [];
      setAllMasterItems(allItems);

      // Filter parent items: has boq_description entries OR (simple bundle + include_in_boq)
      const validParents = allItems.filter(item => {
        const hasBoqEntries = item.boq_description?.entries?.length > 0;
        const isSimpleBundleInBoq =
          item.is_bundle && item.bundle_type === 'simple' && item.include_in_boq;
        return hasBoqEntries || isSimpleBundleInBoq;
      });
      setParentItems(validParents);
      setItemTypes(itemTypesRes.data || []);
      setProjectPhases(phasesRes.data || []);
      setCostTypes(costTypesRes.data || []);
      setBbChapters(chaptersRes.data || []);

      // Index cost types
      const ctMap = {};
      (costTypesRes.data || []).forEach(ct => {
        ctMap[ct.id] = ct.name;
      });
      setCostTypesMap(ctMap);

      // Index old categories
      const catMap = {};
      (categoriesRes.data || []).forEach(c => {
        catMap[c.id] = `${c.category_number} - ${c.name}`;
      });
      setOldCategories(catMap);

      // Build suggestions map from DB rows: parentItemId -> [row, row, ...]
      const sugMap = {};
      (suggestionsRes.data || []).forEach(row => {
        if (!sugMap[row.master_item_id]) sugMap[row.master_item_id] = [];
        sugMap[row.master_item_id].push(row);
      });
      setSuggestions(sugMap);

      // Build reviewed set
      const revSet = new Set((reviewedRes.data || []).map(r => r.master_item_id));
      setReviewedItems(revSet);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Immediate-write actions (like BundleReviewPage) ────────────

  // Helper: write updated base_dependencies to DB and update local state
  const writeDeps = async (parentItem, newDeps) => {
    setSaving(true);
    try {
      const existingOptions = parentItem.item_options || {};
      const updatedOptions = { ...existingOptions, base_dependencies: newDeps };

      const { error } = await supabase
        .from('master_items')
        .update({ item_options: updatedOptions })
        .eq('id', parentItem.id);
      if (error) throw error;

      // Update local state — update in both parentItems and allMasterItems
      const updateItem = item =>
        item.id === parentItem.id ? { ...item, item_options: updatedOptions } : item;
      setParentItems(prev => prev.map(updateItem));
      setAllMasterItems(prev => prev.map(updateItem));
    } catch (err) {
      console.error('Error updating dependencies:', err);
      alert('שגיאה בעדכון: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Add a master_item as base dependency — writes to DB immediately
  const addDependency = async (parentItem, masterItem) => {
    const currentDeps = parentItem.item_options?.base_dependencies || [];
    if (currentDeps.some(d => d.master_item_id === masterItem.id)) return;
    const newDeps = [
      ...currentDeps,
      { master_item_id: masterItem.id, auto_add: true, quantity_type: 'custom' },
    ];
    await writeDeps(parentItem, newDeps);
  };

  // Remove a dependency — writes to DB immediately
  const removeDependency = async (parentItem, masterItemId) => {
    const currentDeps = parentItem.item_options?.base_dependencies || [];
    const newDeps = currentDeps.filter(d => d.master_item_id !== masterItemId);
    await writeDeps(parentItem, newDeps);
  };

  // ─── AI Mapping (Claude API) — persists to dep_mapping_suggestions ─

  const runAiMapping = async () => {
    if (!apiKey) {
      alert('נא להזין Claude API Key (משתנה REACT_APP_CLAUDE_API_KEY)');
      return;
    }

    // Only map items that have no deps AND no pending suggestions in DB
    const unmapped = displayItems.filter(item => {
      if (hasDeps(item)) return false;
      const sRows = suggestions[item.id] || [];
      return !sRows.some(r => r.status === 'pending');
    });
    if (unmapped.length === 0) {
      alert('כל הפריטים כבר ממופים או יש להם הצעות');
      return;
    }

    setAiRunning(true);
    setAiProgress({ current: 0, total: unmapped.length });

    // Build the master items catalog for the prompt (non-bundles only)
    const catalogItems = allMasterItems
      .filter(mi => !mi.is_bundle)
      .map(mi => `- ${mi.item_name} (${mi.id})`)
      .join('\n');

    const BATCH_SIZE = 3;
    let processed = 0;

    for (let i = 0; i < unmapped.length; i += BATCH_SIZE) {
      const batch = unmapped.slice(i, i + BATCH_SIZE);
      if (i > 0) await new Promise(r => setTimeout(r, 3000));

      try {
        const itemsList = batch
          .map((item, idx) => {
            const entries = item.boq_description?.entries || [];
            const descs = entries
              .map(e => e.description || '')
              .filter(Boolean)
              .join('; ');
            return `[${idx}] Parent: "${item.item_name}" | BOQ descriptions: "${descs}"`;
          })
          .join('\n');

        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true',
          },
          body: JSON.stringify({
            model: 'claude-sonnet-4-20250514',
            max_tokens: 2000,
            messages: [
              {
                role: 'user',
                content: `You are mapping Israeli construction/renovation items to their dependencies.

For each parent item below, identify which items from the CATALOG should be auto-created as dependencies when this parent is added to a project.

Dependencies are supporting items needed for the parent to function. For example:
- מזגן אינוורטר (inverter AC) needs: שקע חשמל למזגן, ניקוז, פקט חשמל, גריל
- כיור (sink) needs: נקודת מים, נקודת ניקוז
- תנור needs: נקודת חשמל

ITEMS TO MAP:
${itemsList}

CATALOG OF AVAILABLE DEPENDENCY ITEMS:
${catalogItems}

For each item, select dependency items FROM THE CATALOG ONLY (use their exact UUIDs).
If a dependency clearly should exist but isn't in the catalog, note it in reasoning.

Respond ONLY with a JSON array, one object per item, in order:
[{"dep_ids": ["uuid1", "uuid2"], "dep_names": ["name1", "name2"], "confidence": 0.XX, "reasoning": "brief Hebrew explanation"}, ...]`,
              },
            ],
          }),
        });

        if (!response.ok) {
          const errText = await response.text();
          console.error('[AI DEP MAP] API error:', errText);
          processed += batch.length;
          setAiProgress({ current: processed, total: unmapped.length });
          continue;
        }

        const data = await response.json();
        const text = data.content?.[0]?.text || '';

        const jsonMatch = text.match(/\[[\s\S]*\]/);
        if (!jsonMatch) {
          console.error('[AI DEP MAP] Could not parse response:', text);
          processed += batch.length;
          setAiProgress({ current: processed, total: unmapped.length });
          continue;
        }

        const results = JSON.parse(jsonMatch[0]);

        for (let j = 0; j < batch.length; j++) {
          const parentItem = batch[j];
          const parsed = results[j];
          processed++;
          setAiProgress({ current: processed, total: unmapped.length });

          if (!parsed || !parsed.dep_ids || parsed.dep_ids.length === 0) {
            console.warn(`[AI DEP MAP] No deps suggested for "${parentItem.item_name}"`);
            continue;
          }

          // Validate UUIDs exist in our master items
          const validDeps = [];
          for (let k = 0; k < parsed.dep_ids.length; k++) {
            const depId = parsed.dep_ids[k];
            if (masterItemNameMap[depId]) {
              validDeps.push(depId);
            } else {
              console.warn(
                `[AI DEP MAP] Unknown master_item_id "${depId}" for "${parentItem.item_name}"`,
              );
            }
          }

          if (validDeps.length === 0) continue;

          console.log(
            `[AI DEP MAP] ✓ "${parentItem.item_name}" → [${validDeps.map(id => masterItemNameMap[id]).join(', ')}] (${parsed.confidence})`,
          );

          // Insert rows into dep_mapping_suggestions table
          const rowsToInsert = validDeps.map(depId => ({
            master_item_id: parentItem.id,
            suggested_dep_id: depId,
            confidence: parsed.confidence,
            ai_reasoning: parsed.reasoning,
            status: 'pending',
          }));

          const { data: insertedRows, error: insertErr } = await supabase
            .from('dep_mapping_suggestions')
            .upsert(rowsToInsert, { onConflict: 'master_item_id,suggested_dep_id' })
            .select('*');

          if (insertErr) {
            console.error('[AI DEP MAP] DB insert error:', insertErr);
            continue;
          }

          // Update local suggestions state
          setSuggestions(prev => ({
            ...prev,
            [parentItem.id]: [...(prev[parentItem.id] || []), ...(insertedRows || rowsToInsert)],
          }));
        }
      } catch (err) {
        console.error('[AI DEP MAP] Error processing batch:', err);
        processed += batch.length;
        setAiProgress({ current: processed, total: unmapped.length });
      }
    }

    setAiRunning(false);
  };

  // ─── Approve / Reject (DB-persisted) ───────────────────────────

  // Approve a single suggested dep — writes dep to base_dependencies, updates suggestion row status
  const handleApproveOneDep = async (parentItem, suggestionRow) => {
    setSaving(true);
    try {
      // Write this dep to base_dependencies
      const currentDeps = parentItem.item_options?.base_dependencies || [];
      if (!currentDeps.some(d => d.master_item_id === suggestionRow.suggested_dep_id)) {
        const newDeps = [
          ...currentDeps,
          {
            master_item_id: suggestionRow.suggested_dep_id,
            auto_add: true,
            quantity_type: 'custom',
          },
        ];
        await writeDeps(parentItem, newDeps);
      }

      // Update suggestion row status in DB
      const { error } = await supabase
        .from('dep_mapping_suggestions')
        .update({ status: 'approved' })
        .eq('id', suggestionRow.id);
      if (error) throw error;

      // Update local state
      setSuggestions(prev => ({
        ...prev,
        [parentItem.id]: (prev[parentItem.id] || []).map(r =>
          r.id === suggestionRow.id ? { ...r, status: 'approved' } : r,
        ),
      }));
    } catch (err) {
      console.error('Error approving suggestion:', err);
      alert('שגיאה באישור: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Reject a single suggested dep — updates suggestion row status
  const handleRejectOneDep = async (parentItemId, suggestionRow) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('dep_mapping_suggestions')
        .update({ status: 'rejected' })
        .eq('id', suggestionRow.id);
      if (error) throw error;

      setSuggestions(prev => ({
        ...prev,
        [parentItemId]: (prev[parentItemId] || []).map(r =>
          r.id === suggestionRow.id ? { ...r, status: 'rejected' } : r,
        ),
      }));
    } catch (err) {
      console.error('Error rejecting suggestion:', err);
      alert('שגיאה בדחייה: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Approve ALL pending suggestions for a parent
  const handleApproveAllDeps = async parentItem => {
    const rows = (suggestions[parentItem.id] || []).filter(r => r.status === 'pending');
    if (rows.length === 0) return;

    setSaving(true);
    try {
      // Write all deps to base_dependencies
      const currentDeps = parentItem.item_options?.base_dependencies || [];
      const newDeps = [...currentDeps];
      for (const row of rows) {
        if (!newDeps.some(d => d.master_item_id === row.suggested_dep_id)) {
          newDeps.push({
            master_item_id: row.suggested_dep_id,
            auto_add: true,
            quantity_type: 'custom',
          });
        }
      }
      await writeDeps(parentItem, newDeps);

      // Update all suggestion rows status in DB
      const rowIds = rows.map(r => r.id);
      const { error } = await supabase
        .from('dep_mapping_suggestions')
        .update({ status: 'approved' })
        .in('id', rowIds);
      if (error) throw error;

      setSuggestions(prev => ({
        ...prev,
        [parentItem.id]: (prev[parentItem.id] || []).map(r =>
          rowIds.includes(r.id) ? { ...r, status: 'approved' } : r,
        ),
      }));
    } catch (err) {
      console.error('Error approving all:', err);
      alert('שגיאה באישור: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Reject ALL pending suggestions for a parent
  const handleRejectAllDeps = async parentItemId => {
    const rows = (suggestions[parentItemId] || []).filter(r => r.status === 'pending');
    if (rows.length === 0) return;

    setSaving(true);
    try {
      const rowIds = rows.map(r => r.id);
      const { error } = await supabase
        .from('dep_mapping_suggestions')
        .update({ status: 'rejected' })
        .in('id', rowIds);
      if (error) throw error;

      setSuggestions(prev => ({
        ...prev,
        [parentItemId]: (prev[parentItemId] || []).map(r =>
          rowIds.includes(r.id) ? { ...r, status: 'rejected' } : r,
        ),
      }));
    } catch (err) {
      console.error('Error rejecting all:', err);
      alert('שגיאה בדחייה: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Batch approve all high-confidence pending suggestions
  const handleBatchApprove = async () => {
    // Find all parent items that have pending suggestions with confidence >= 0.8
    const eligibleParents = displayItems.filter(item => {
      const rows = (suggestions[item.id] || []).filter(r => r.status === 'pending');
      return rows.length > 0 && rows.some(r => r.confidence >= 0.8);
    });

    if (eligibleParents.length === 0) {
      alert('אין הצעות ברמת ביטחון גבוהה לאישור');
      return;
    }

    if (
      !window.confirm(`לאשר הצעות ברמת ביטחון גבוהה (≥0.8) עבור ${eligibleParents.length} פריטים?`)
    )
      return;

    for (const item of eligibleParents) {
      await handleApproveAllDeps(item);
    }
  };

  // ─── Handle new master item creation ─────────────────────────────
  const handleItemCreated = newItem => {
    setAllMasterItems(prev =>
      [...prev, newItem].sort((a, b) => a.item_name.localeCompare(b.item_name)),
    );
    if (createModalCallback) {
      createModalCallback(newItem);
      setCreateModalCallback(null);
    }
  };

  // ─── Computed values ─────────────────────────────────────────────

  // Build a lookup: master_item_id → item_name
  const masterItemNameMap = useMemo(() => {
    const map = {};
    allMasterItems.forEach(mi => {
      map[mi.id] = mi.item_name;
    });
    return map;
  }, [allMasterItems]);

  // Build a lookup: master_item_id → is_bundle
  const masterItemBundleMap = useMemo(() => {
    const map = {};
    allMasterItems.forEach(mi => {
      map[mi.id] = mi.is_bundle;
    });
    return map;
  }, [allMasterItems]);

  // Build a lookup: master_item_id → full item object (for resolving bundle children)
  const allItemsById = useMemo(() => {
    const map = {};
    allMasterItems.forEach(mi => {
      map[mi.id] = mi;
    });
    return map;
  }, [allMasterItems]);

  // Build a set of all child IDs that belong to any bundle (to exclude from top-level)
  const bundleChildIds = useMemo(() => {
    const ids = new Set();
    parentItems.forEach(item => {
      if (item.is_bundle && item.bundle_items?.items) {
        item.bundle_items.items.forEach(c => ids.add(c.item_id));
      }
    });
    return ids;
  }, [parentItems]);

  // Build the display list: non-bundle items + bundle items (children removed from top level)
  // Bundles get a `resolvedChildren` array attached
  const displayItems = useMemo(() => {
    const result = [];
    parentItems.forEach(item => {
      // Skip items that are children of a bundle
      if (bundleChildIds.has(item.id)) return;

      if (item.is_bundle && item.bundle_items?.items) {
        // Resolve all children (even ones without boq_description)
        const children = (item.bundle_items.items || [])
          .map(c => allItemsById[c.item_id])
          .filter(Boolean);
        result.push({ ...item, resolvedChildren: children });
      } else {
        result.push({ ...item, resolvedChildren: null });
      }
    });
    return result;
  }, [parentItems, bundleChildIds, allItemsById]);

  // Check if an item has any base_dependencies
  const hasDeps = useCallback(item => {
    return (item.item_options?.base_dependencies || []).length > 0;
  }, []);

  // Get pending suggestion count for an item
  const getPendingCount = useCallback(
    itemId => {
      return (suggestions[itemId] || []).filter(r => r.status === 'pending').length;
    },
    [suggestions],
  );

  const stats = useMemo(() => {
    const total = displayItems.length;
    const mapped = displayItems.filter(item => hasDeps(item)).length;
    const pending = displayItems.filter(item => getPendingCount(item.id) > 0).length;
    return { total, mapped, unmapped: total - mapped, pending };
  }, [displayItems, hasDeps, getPendingCount]);

  // Filtered display items
  const filteredItems = useMemo(() => {
    let items = displayItems;

    switch (filter) {
      case 'todo':
        items = items.filter(item => !reviewedItems.has(item.id));
        break;
      case 'unmapped':
        items = items.filter(item => !hasDeps(item));
        break;
      case 'mapped':
        items = items.filter(item => hasDeps(item));
        break;
      case 'pending':
        items = items.filter(item => getPendingCount(item.id) > 0);
        break;
      case 'bundles':
        items = items.filter(item => item.is_bundle);
        break;
      case 'reviewed':
        items = items.filter(item => reviewedItems.has(item.id));
        break;
      default:
        break;
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(item => {
        if (item.item_name.toLowerCase().includes(term)) return true;
        if (
          (item.boq_description?.entries || []).some(e =>
            e.description?.toLowerCase().includes(term),
          )
        )
          return true;
        // Also search within bundle children
        if (item.resolvedChildren) {
          return item.resolvedChildren.some(c => c.item_name.toLowerCase().includes(term));
        }
        return false;
      });
    }

    return items;
  }, [displayItems, filter, searchTerm, hasDeps, getPendingCount, reviewedItems]);

  // ─── Toggle reviewed ("done") ─────────────────────────────────────
  const toggleReviewed = async itemId => {
    const isReviewed = reviewedItems.has(itemId);
    try {
      if (isReviewed) {
        // Remove from reviewed
        const { error } = await supabase
          .from('dep_mapping_reviewed')
          .delete()
          .eq('master_item_id', itemId);
        if (error) throw error;
        setReviewedItems(prev => {
          const next = new Set(prev);
          next.delete(itemId);
          return next;
        });
      } else {
        // Mark as reviewed
        const { error } = await supabase
          .from('dep_mapping_reviewed')
          .insert({ master_item_id: itemId });
        if (error) throw error;
        setReviewedItems(prev => new Set(prev).add(itemId));
      }
    } catch (err) {
      console.error('Error toggling reviewed:', err);
      alert('שגיאה בעדכון: ' + err.message);
    }
  };

  // ─── Toggle bundle expand/collapse ───────────────────────────────
  const toggleExpand = bundleId => {
    setExpandedBundles(prev => {
      const next = new Set(prev);
      if (next.has(bundleId)) next.delete(bundleId);
      else next.add(bundleId);
      return next;
    });
  };

  // ─── Render: single item row (handles regular, bundle parent, and child rows) ─
  const renderItemRow = (
    item,
    idx,
    { isChild = false, isBundle = false, isExpanded = false } = {},
  ) => {
    const entries = item.boq_description?.entries || [];
    const currentDeps = item.item_options?.base_dependencies || [];
    const depIds = currentDeps.map(d => d.master_item_id);
    const excludeIds = [...depIds, item.id];
    const pendingSuggestions = (suggestions[item.id] || []).filter(r => r.status === 'pending');
    const approvedSuggestions = (suggestions[item.id] || []).filter(r => r.status === 'approved');
    const rejectedSuggestions = (suggestions[item.id] || []).filter(r => r.status === 'rejected');

    // Bundle parent rows are clickable to expand/collapse; interactive cells use stopPropagation
    const rowClick = isBundle ? () => toggleExpand(item.id) : undefined;
    const cellGuard = isBundle ? e => e.stopPropagation() : undefined;

    // Row background: bundle parent = light purple, child = light gray, normal = transparent
    const rowBg = isBundle ? '#f0f0ff' : isChild ? '#f8fafc' : 'transparent';

    return (
      <tr
        key={item.id}
        style={{
          verticalAlign: 'top',
          background: rowBg,
          cursor: isBundle ? 'pointer' : 'default',
        }}
        onClick={rowClick}
      >
        {/* # column */}
        <td style={{ paddingRight: isChild ? 24 : 0 }}>
          {isBundle && (
            <span style={{ fontSize: 10, marginLeft: 4 }}>{isExpanded ? '▼' : '▶'}</span>
          )}
          {isChild ? '↳' : idx + 1}
        </td>

        {/* Item name column */}
        <td>
          <strong>{item.item_name}</strong>
          {item.is_bundle && (
            <span
              style={{
                display: 'inline-block',
                fontSize: 9,
                color: '#fff',
                background: '#8b5cf6',
                borderRadius: 3,
                padding: '0 4px',
                marginRight: 4,
              }}
            >
              {isBundle ? `חבילה (${item.resolvedChildren?.length || 0} ילדים)` : 'חבילה'}
            </span>
          )}
          {isChild && (
            <span
              style={{
                display: 'inline-block',
                fontSize: 9,
                color: '#6b7280',
                background: '#e5e7eb',
                borderRadius: 3,
                padding: '0 4px',
                marginRight: 4,
              }}
            >
              ילד
            </span>
          )}
          {item.include_in_boq && (
            <span style={{ display: 'block', fontSize: 10, color: '#16a34a' }}>BOQ</span>
          )}
        </td>

        {/* BOQ description entries column */}
        <td onClick={cellGuard}>
          {entries.length > 0 ? (
            entries.map((entry, entryIdx) => (
              <div
                key={entryIdx}
                style={{
                  padding: '4px 0',
                  borderBottom: entryIdx < entries.length - 1 ? '1px solid #f1f5f9' : 'none',
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>
                    {entry.description || '(ללא תיאור)'}
                  </span>
                  {entry.category_id && (
                    <span className="item-type-badge" style={{ fontSize: 10 }}>
                      {oldCategories[entry.category_id] || ''}
                    </span>
                  )}
                  {entry.measurement_type && (
                    <span className="item-type-badge" style={{ fontSize: 10 }}>
                      {costTypesMap[entry.measurement_type] || ''}
                    </span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <span style={{ fontSize: 11, color: '#94a3b8' }}>אין תיאורי BOQ</span>
          )}
        </td>

        {/* Dependencies + AI suggestions column */}
        <td onClick={cellGuard}>
          {/* Current saved deps */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
              alignItems: 'center',
              marginBottom: pendingSuggestions.length > 0 ? 8 : 0,
            }}
          >
            {currentDeps.map(dep => (
              <span
                key={dep.master_item_id}
                className="item-type-badge status-approved"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}
              >
                {masterItemNameMap[dep.master_item_id] || dep.master_item_id}
                {masterItemBundleMap[dep.master_item_id] && (
                  <span style={{ fontSize: 8, color: '#8b5cf6' }}>חב</span>
                )}
                <button
                  onClick={() => removeDependency(item, dep.master_item_id)}
                  disabled={saving}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: 12,
                    color: '#c00',
                    lineHeight: 1,
                  }}
                  title="הסר"
                >
                  ✕
                </button>
              </span>
            ))}
            {currentDeps.length === 0 && pendingSuggestions.length === 0 && (
              <span style={{ fontSize: 11, color: '#94a3b8' }}>אין תלויות</span>
            )}
            {/* Manual add picker */}
            <div style={{ position: 'relative' }}>
              <MasterItemPicker
                allMasterItems={allMasterItems}
                excludeIds={excludeIds}
                onSelect={mi => addDependency(item, mi)}
                onCreateNew={() => {
                  setCreateModalCallback(() => newItem => addDependency(item, newItem));
                  setShowCreateModal(true);
                }}
              />
            </div>
          </div>

          {/* Pending AI suggestions */}
          {pendingSuggestions.length > 0 && (
            <div
              style={{
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: 6,
                padding: '6px 8px',
                marginTop: 4,
              }}
            >
              <div style={{ fontSize: 10, color: '#92400e', marginBottom: 4, fontWeight: 600 }}>
                הצעות AI ({pendingSuggestions.length} פריטים
                {pendingSuggestions[0]?.confidence
                  ? `, ${(pendingSuggestions[0].confidence * 100).toFixed(0)}%`
                  : ''}
                )
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
                {pendingSuggestions.map(row => (
                  <span
                    key={row.id || row.suggested_dep_id}
                    className="item-type-badge"
                    style={{
                      fontSize: 11,
                      background: '#fef3c7',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {masterItemNameMap[row.suggested_dep_id] || row.suggested_dep_id}
                    <button
                      onClick={() => handleApproveOneDep(item, row)}
                      disabled={saving}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: 13,
                        color: '#16a34a',
                        lineHeight: 1,
                      }}
                      title="אשר"
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => handleRejectOneDep(item.id, row)}
                      disabled={saving}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: 12,
                        color: '#dc2626',
                        lineHeight: 1,
                      }}
                      title="דחה"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
              {pendingSuggestions[0]?.ai_reasoning && (
                <div style={{ fontSize: 10, color: '#78716c' }}>
                  {pendingSuggestions[0].ai_reasoning}
                </div>
              )}
            </div>
          )}
          {/* Previously approved/rejected counts */}
          {approvedSuggestions.length > 0 && (
            <div style={{ fontSize: 10, color: '#16a34a', marginTop: 4 }}>
              ✓ {approvedSuggestions.length} הצעות אושרו
            </div>
          )}
          {rejectedSuggestions.length > 0 && (
            <div style={{ fontSize: 10, color: '#dc2626', marginTop: 2 }}>
              ✗ {rejectedSuggestions.length} הצעות נדחו
            </div>
          )}
        </td>

        {/* Actions column */}
        <td onClick={cellGuard}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {pendingSuggestions.length > 0 && (
              <>
                <button
                  className="bb-action-btn approve"
                  style={{ fontSize: 10 }}
                  onClick={() => handleApproveAllDeps(item)}
                  disabled={saving}
                >
                  אשר הכל
                </button>
                <button
                  className="bb-action-btn reject"
                  style={{ fontSize: 10 }}
                  onClick={() => handleRejectAllDeps(item.id)}
                  disabled={saving}
                >
                  דחה הכל
                </button>
              </>
            )}
            {pendingSuggestions.length === 0 && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: currentDeps.length > 0 ? '#16a34a' : '#94a3b8',
                  textAlign: 'center',
                }}
              >
                {currentDeps.length} תלויות
              </span>
            )}
            {/* Mark as done / undo */}
            {!isChild && (
              <button
                onClick={() => toggleReviewed(item.id)}
                style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  borderRadius: 4,
                  border: '1px solid #d1d5db',
                  background: reviewedItems.has(item.id) ? '#dcfce7' : '#fff',
                  color: reviewedItems.has(item.id) ? '#16a34a' : '#6b7280',
                  cursor: 'pointer',
                }}
                title={reviewedItems.has(item.id) ? 'סמן כלא סיים' : 'סמן כסיים'}
              >
                {reviewedItems.has(item.id) ? '✓ סיים' : 'סמן סיים'}
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // ─── Render ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bb-mapping-page">
        <div className="bb-mapping-loading">טוען נתונים...</div>
      </div>
    );
  }

  return (
    <div className="bb-mapping-page">
      {/* Header */}
      <div className="bb-mapping-header">
        <h1>מיפוי תלויות כתב כמויות → פריטי מאסטר</h1>
        <div className="bb-mapping-header-actions">
          <input
            type="text"
            className="bb-mapping-search"
            placeholder="חיפוש פריט או תיאור..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Progress */}
      <div className="bb-mapping-progress">
        <div className="bb-mapping-progress-bar">
          <div
            className="bb-mapping-progress-fill"
            style={{ width: `${stats.total ? (stats.mapped / stats.total) * 100 : 0}%` }}
          />
        </div>
        <div className="bb-mapping-progress-text">
          {stats.mapped}/{stats.total} ממופים
          {saving && ' | שומר...'}
        </div>
      </div>

      {/* AI actions */}
      <div className="api-key-section">
        <span className="api-key-label">
          Claude API: {apiKey ? '✓ מחובר' : '✗ חסר REACT_APP_CLAUDE_API_KEY ב-.env'}
        </span>
        <button
          className="bb-action-btn primary"
          onClick={runAiMapping}
          disabled={aiRunning || !apiKey}
        >
          {aiRunning
            ? `ממפה... (${aiProgress.current}/${aiProgress.total})`
            : `הרץ מיפוי AI (${stats.unmapped - stats.pending} חסרים)`}
        </button>
        <button
          className="bb-action-btn approve"
          onClick={handleBatchApprove}
          disabled={aiRunning || stats.pending === 0}
        >
          אשר הכל (ביטחון ≥0.8)
        </button>
      </div>

      {/* Filters */}
      <div className="bb-mapping-filters">
        {[
          {
            key: 'todo',
            label: `לטיפול (${displayItems.filter(i => !reviewedItems.has(i.id)).length})`,
          },
          { key: 'all', label: `הכל (${stats.total})` },
          { key: 'unmapped', label: `לא ממופים (${stats.unmapped})` },
          { key: 'pending', label: `ממתינים (${stats.pending})` },
          { key: 'mapped', label: `ממופים (${stats.mapped})` },
          { key: 'bundles', label: `חבילות (${displayItems.filter(i => i.is_bundle).length})` },
          { key: 'reviewed', label: `סיימו (${reviewedItems.size})` },
        ].map(f => (
          <button
            key={f.key}
            className={`bb-mapping-filter-btn ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bb-mapping-table-container">
        <table className="bb-mapping-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th style={{ width: 160 }}>פריט מקור</th>
              <th>תיאורי כתב כמויות</th>
              <th>תלויות ממופות / הצעות AI</th>
              <th style={{ width: 100 }}>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={5} className="bb-mapping-empty">
                  אין פריטים להצגה
                </td>
              </tr>
            ) : (
              filteredItems.map((item, idx) => {
                const isBundle = item.is_bundle && item.resolvedChildren;
                const isExpanded = expandedBundles.has(item.id);

                return (
                  <React.Fragment key={item.id}>
                    {renderItemRow(item, idx, { isBundle, isExpanded })}
                    {/* Bundle children (expanded) */}
                    {isBundle &&
                      isExpanded &&
                      item.resolvedChildren.map(child =>
                        renderItemRow(child, idx, { isChild: true }),
                      )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Create Master Item Modal */}
      {showCreateModal && (
        <CreateMasterItemModal
          onClose={() => {
            setShowCreateModal(false);
            setCreateModalCallback(null);
          }}
          onCreated={handleItemCreated}
          itemTypes={itemTypes}
          projectPhases={projectPhases}
          costTypes={costTypes}
          bbChapters={bbChapters}
        />
      )}
    </div>
  );
};

export default BOQDepMappingPage;
